import React, { useEffect, useState } from "react";
import { api } from "../../services/api";
import type { NewsItem } from "../../types/news";
import { Skeleton, SkeletonText } from "../Skeleton";
import styles from "./NewsSection.module.scss";

interface NewsSectionProps {
  gameId: string;
}

type SectionState = "loading" | "success" | "error" | "empty";

/**
 * NewsSection — Fetches and displays Steam news articles for a game.
 * Renders independently from the main game data to avoid blocking page load.
 *
 * States:
 * - Loading: skeleton placeholders
 * - Success: news cards with title, author, date, content preview
 * - Error: inline "News unavailable" message
 * - Empty: returns null (section not rendered)
 */
const NewsSection: React.FC<NewsSectionProps> = ({ gameId }) => {
  const [state, setState] = useState<SectionState>("loading");
  const [news, setNews] = useState<NewsItem[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchNews = async () => {
      setState("loading");
      try {
        const response = await api.games.getNewsForGame(gameId);
        if (cancelled) return;

        if (response.news.length === 0) {
          setState("empty");
        } else {
          setNews(response.news);
          setState("success");
        }
      } catch {
        if (!cancelled) {
          setState("error");
        }
      }
    };

    fetchNews();

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  if (state === "empty") {
    return null;
  }

  if (state === "loading") {
    return (
      <section className={styles.newsSection} data-testid="news-section-loading">
        <h2 className={styles.heading}>News</h2>
        <div className={styles.cardList}>
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.card} aria-hidden="true">
              <Skeleton height="1.25rem" width="80%" />
              <Skeleton height="0.75rem" width="40%" />
              <SkeletonText lines={2} lineHeight="0.75rem" gap="0.4rem" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (state === "error") {
    return (
      <section className={styles.newsSection} data-testid="news-section-error">
        <h2 className={styles.heading}>News</h2>
        <p className={styles.errorMessage}>News unavailable</p>
      </section>
    );
  }

  return (
    <section className={styles.newsSection} data-testid="news-section">
      <h2 className={styles.heading}>News</h2>
      <div className={styles.cardList}>
        {news.map((item) => (
          <article key={item.gid} className={styles.card}>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.cardTitle}
            >
              {item.title}
            </a>
            <div className={styles.cardMeta}>
              {item.author && <span className={styles.author}>{item.author}</span>}
              <time className={styles.date} dateTime={new Date(item.date * 1000).toISOString()}>
                {new Date(item.date * 1000).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </time>
            </div>
            <p className={styles.contentPreview}>
              {truncateContent(item.contents, 150)}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
};

/**
 * Truncates text content to a maximum length, appending ellipsis if truncated.
 */
function truncateContent(text: string, maxLength: number): string {
  if (!text) return "";
  // Strip basic HTML/BBCode tags for cleaner preview
  const cleaned = text.replace(/\[.*?\]|<.*?>/g, "").trim();
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.slice(0, maxLength).trimEnd() + "…";
}

export default NewsSection;
